import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column from 'components/Column'
import { FarmConfig } from 'components/KodiakFarms/types'
import { RewardTokenChange } from 'components/KodiakFarms/utils/types'
import Row from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { intervalToDuration } from 'date-fns'
import formatDuration from 'date-fns/formatDuration'
import { RefreshCw } from 'react-feather'
import styled from 'styled-components'

const StyledListItem = styled(Row)`
  padding: 12px 0;
  gap: 12px;
  align-items: flex-start;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
  }
`

const ChangeIcon = styled(RefreshCw)`
  color: ${({ theme }) => theme.accentAction};
  background: ${({ theme }) => `${theme.accentAction}1A`};
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  min-width: 24px;
`

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0 0 24px 0;
  line-height: 1.5;
`

const ChangeText = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.textPrimary};
`

const ValueChange = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
`

type UpdateFarmStepProps = {
  next: () => void
  onStart: () => Promise<void>
  config: FarmConfig
  originalConfig: FarmConfig
  changedFields: Array<{ field: string; current: string; previous: string }>
  changedRewardTokens: Array<{
    index: number
    changes: RewardTokenChange[]
  } | null>
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

export default function UpdateFarmConfigStep({
  next,
  onStart,
  config,
  originalConfig,
  changedFields,
  changedRewardTokens,
  ...rest
}: UpdateFarmStepProps) {
  const formatValue = (value: string, field: string) => {
    if (field.includes('Duration') || field.includes('Time')) {
      const seconds = parseInt(value)
      const duration = intervalToDuration({ start: 0, end: seconds * 1000 })
      return formatDuration(duration)
    }
    if (field.includes('Cap')) {
      return value === '-1' ? 'Unlimited' : value
    }
    if (field.includes('Multiplier')) {
      return `${value}x`
    }
    return value
  }

  return (
    <ConfirmationTransactionStep {...rest} onConfirm={next}>
      <Column width="100%" gap="24px">
        <Description>Review the following configuration changes before updating the farm:</Description>

        <LightCard>
          {/* General Fields Changes */}
          {changedFields.map(({ field, current, previous }, index) => (
            <StyledListItem key={`general-${index}`}>
              <ChangeIcon />
              <Column gap="4px">
                <ChangeText>Updated {field}</ChangeText>
                <ValueChange>
                  From {formatValue(previous, field)} to {formatValue(current, field)}
                </ValueChange>
              </Column>
            </StyledListItem>
          ))}

          {/* Reward Token Changes */}
          {changedRewardTokens
            .filter((token): token is NonNullable<typeof token> => token !== null)
            .flatMap((rewardToken) =>
              rewardToken.changes.map(({ field, current, previous }, index) => {
                const isNewToken = previous === 'None'
                const title = isNewToken
                  ? `Added new reward token ${field}`
                  : `Changed Token ${rewardToken.index + 1} ${field}`
                const changeDescription = isNewToken ? `Set to ${current}` : `From ${previous} to ${current}`

                return (
                  <StyledListItem key={`token-${rewardToken.index}-${index}`}>
                    <ChangeIcon />
                    <Column gap="4px">
                      <ChangeText>{title}</ChangeText>
                      <ValueChange>{changeDescription}</ValueChange>
                    </Column>
                  </StyledListItem>
                )
              })
            )}
        </LightCard>

        <ButtonPrimary onClick={onStart} style={{ marginTop: '8px' }}>
          Update Farm Configuration
        </ButtonPrimary>
      </Column>
    </ConfirmationTransactionStep>
  )
}
